/**
 * Modal de edición de gasto — Sprint 17 S17-Z14.
 *
 * Permite editar fecha, categoría, monto, proveedor, descripción y URL
 * de comprobante de un gasto existente. NO permite editar gastos:
 * - anulados (botón "Editar" se oculta desde la lista).
 * - generados por aplicar_compensacion (compensacion_id != null) —
 *   backend rechaza con 409 y aquí mostramos el error si llega.
 *
 * Doctrina: para corregir un gasto por compensación, anular la
 * compensación y rehacerla. Esto mantiene la coherencia contable
 * entre Gasto + Pago virtual + DistribucionPago.
 */
import { useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  actualizarGasto,
  type GastoRead,
  type GastoUpdateInput,
} from '../../lib/api/gastos';
import type { CategoriaRead } from '../../lib/api/types';
import { Button } from '../ui/Button';
import { InputFecha } from '../ui/InputFecha';
import { InputMontoCLP } from '../ui/InputMontoCLP';
import { Modal } from '../ui/Modal';
import { CategoriaSelector } from './CategoriaSelector';

export interface ModalEditarGastoProps {
  gasto: GastoRead;
  categorias: CategoriaRead[];
  onClose: () => void;
  onActualizado: (g: GastoRead) => void;
}

export function ModalEditarGasto({
  gasto,
  categorias,
  onClose,
  onActualizado,
}: ModalEditarGastoProps) {
  const [fecha, setFecha] = useState(gasto.fecha);
  const [categoriaId, setCategoriaId] = useState(gasto.categoria_id);
  const [monto, setMonto] = useState<number | null>(gasto.monto);
  const [proveedor, setProveedor] = useState(gasto.proveedor);
  const [descripcion, setDescripcion] = useState(gasto.descripcion);
  const [comprobanteUrl, setComprobanteUrl] = useState(
    gasto.comprobante_url ?? '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categoriasGasto = categorias.filter(
    (c) => c.tipo === 'gasto' && c.activa,
  );

  // Cambió algo respecto al original.
  const cambio =
    fecha !== gasto.fecha ||
    categoriaId !== gasto.categoria_id ||
    monto !== gasto.monto ||
    proveedor.trim() !== gasto.proveedor ||
    descripcion.trim() !== gasto.descripcion ||
    (comprobanteUrl.trim() || null) !== gasto.comprobante_url;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!categoriaId) return setError('Selecciona una categoría');
    if (monto === null || monto <= 0)
      return setError('El monto debe ser mayor que 0');
    if (!proveedor.trim()) return setError('Proveedor requerido');
    if (!descripcion.trim()) return setError('Descripción requerida');
    if (!cambio) return setError('No hay cambios que guardar');

    // Solo enviar campos que cambiaron — backend acepta PATCH parcial.
    const patch: GastoUpdateInput = {};
    if (fecha !== gasto.fecha) patch.fecha = fecha;
    if (categoriaId !== gasto.categoria_id) patch.categoria_id = categoriaId;
    if (monto !== gasto.monto) patch.monto = monto;
    if (proveedor.trim() !== gasto.proveedor)
      patch.proveedor = proveedor.trim();
    if (descripcion.trim() !== gasto.descripcion)
      patch.descripcion = descripcion.trim();
    const newCompUrl = comprobanteUrl.trim() || null;
    if (newCompUrl !== gasto.comprobante_url)
      patch.comprobante_url = newCompUrl;

    setSubmitting(true);
    try {
      const actualizado = await actualizarGasto(gasto.id, patch);
      onActualizado(actualizado);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          err.body && typeof err.body === 'object' && 'detail' in err.body
            ? String(err.body.detail)
            : `Error ${err.status} al guardar`,
        );
      } else {
        setError('No pudimos guardar los cambios. Intenta nuevamente.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Editar gasto"
      onClose={() => {
        if (!submitting) onClose();
      }}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={(e) =>
              handleSubmit(e as unknown as FormEvent<HTMLFormElement>)
            }
            disabled={submitting || !cambio}
          >
            {submitting ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-xs text-text-muted block">Fecha</span>
            <InputFecha
              value={fecha}
              onChange={setFecha}
              className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
            />
          </label>
          <label className="text-sm space-y-1">
            <span className="text-xs text-text-muted block">Monto</span>
            <InputMontoCLP
              value={monto}
              onChange={setMonto}
              placeholder="0"
              className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
            />
          </label>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-xs text-text-muted block">Categoría</span>
          <CategoriaSelector
            categorias={categoriasGasto}
            value={categoriaId}
            onChange={setCategoriaId}
            placeholder="Seleccionar…"
            className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
          />
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-xs text-text-muted block">Proveedor</span>
          <input
            type="text"
            value={proveedor}
            onChange={(e) => setProveedor(e.target.value)}
            placeholder="Ej. Aguas Andinas, Sodimac, Guillermo Pérez…"
            className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
          />
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-xs text-text-muted block">Descripción</span>
          <textarea
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            placeholder="Detalle del gasto…"
            rows={3}
            className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange resize-none"
          />
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-xs text-text-muted block">
            URL del comprobante (opcional)
          </span>
          <input
            type="url"
            value={comprobanteUrl}
            onChange={(e) => setComprobanteUrl(e.target.value)}
            placeholder="https://…"
            className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
          />
        </label>

        {error && (
          <div className="text-sm text-state-danger bg-state-danger/10 border-l-4 border-state-danger px-3 py-2 rounded-sm">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
